import { useState, useEffect, useRef } from 'react';
import packageJson from '../../package.json';
import SmartBuyLogo from '../images/SB.png';
import MasLogo from '../images/MAS.png';

interface OTPVerificationProps {
  email: string;
  onVerify: (otp: string) => void;
  onBack: () => void;
  onResend: () => void;
  error?: string;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ 
  email, 
  onVerify, 
  onBack, 
  onResend,
  error: parentError
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Version information
  const version = `v${packageJson.version}`;

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
    return undefined;
  }, [timeLeft]);

  // Auto focus first input when component mounts
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1 || isVerified) return; // Prevent multiple characters and changes after verification
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Remove auto-submit to prevent double verification
    // User must click the verify button manually
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Enter tuşuna basıldığında doğrula butonunu çalıştır
    if (e.key === 'Enter') {
      e.preventDefault();
      handleVerify();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtp[i] = pastedData[i] || '';
    }
    
    setOtp(newOtp);
    
    // Focus the next empty field or the last field
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerify = async (otpCode?: string) => {
    const codeToVerify = otpCode || otp.join('');
    if (codeToVerify.length !== 6 || isVerified) return;

    setIsLoading(true);
    setError('');
    
    try {
      // Mark as verified to prevent multiple submissions
      setIsVerified(true);
      
      // Call the parent's onVerify callback (parent will handle API call)
      onVerify(codeToVerify);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'OTP doğrulanamadı');
      // Clear OTP fields on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setIsVerified(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || isVerified) return;
    
    setTimeLeft(60);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    setError('');
    setIsVerified(false);
    inputRefs.current[0]?.focus();
    
    try {
      await onResend();
    } catch (error) {
      setError('OTP tekrar gönderilemedi');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="blue-geometric-bg dark:bg-gray-900 min-h-screen font-inter transition-colors duration-300">
      {/* MasLogo in top left */}
      <div className="absolute top-6 left-6 z-20">
        <img 
          src={MasLogo} 
          alt="MAS Logo" 
          className="h-12 w-auto"
        />
      </div>

      {/* Geometric Shapes */}
      <div className="geometric-shape-1"></div>
      <div className="geometric-shape-2"></div>
      <div className="geometric-shape-3"></div>
      <div className="geometric-shape-4"></div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md border border-blue-100 dark:border-gray-700 transition-colors duration-300">
          
          {/* Logo Section */}
          <div className="text-center mb-8 mt-4">
            <div className="flex flex-col items-center justify-center mx-auto">
              <img 
                src={SmartBuyLogo} 
                alt="SmartBuy" 
                className="h-20 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white mt-0 transition-colors">Satınalma & Talep Yönetimi</h1>
              <p className="text-gray-500 dark:text-gray-400 text-xs transition-colors">Doğrulama kodu girin</p>
            </div>
          </div>

          {/* OTP Form */}
          <div className="space-y-6">
            
            {/* Error Message */}
            {(error || parentError) && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>
                  <p className="text-sm text-red-700 dark:text-red-300">{error || parentError}</p>
                </div>
              </div>
            )}
            
            {/* Email Display */}
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
                Doğrulama kodu gönderildi
              </p>
              <p className="text-sm font-medium text-gray-800 dark:text-white transition-colors">
                {email}
              </p>
            </div>

            {/* OTP Input Fields */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors text-center">
                6 haneli doğrulama kodunu girin
              </label>
              
              <div className="flex justify-center space-x-3" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 text-gray-900 dark:text-white ${
                      isVerified 
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20 cursor-not-allowed' 
                        : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600'
                    }`}
                    disabled={isVerified}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
            </div>

            {/* Timer and Resend */}
            <div className="text-center space-y-2">
              {!canResend ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">
                  Yeni kod gönderebilmek için {formatTime(timeLeft)} bekleyin
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-sm text-primary hover:text-secondary transition-colors font-medium"
                >
                  Yeni kod gönder
                </button>
              )}
            </div>

            {/* Verify Button */}
            <button 
              type="button"
              onClick={() => handleVerify()}
              disabled={isLoading || otp.some(digit => digit === '') || isVerified}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-blue-600 focus:ring-4 focus:ring-blue-200 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Doğrulanıyor...
                </>
              ) : isVerified ? (
                <>
                  <i className="fas fa-check-circle mr-2"></i>
                  Doğrulandı
                </>
              ) : (
                <>
                  <i className="fas fa-check mr-2"></i>
                  Doğrula
                </>
              )}
            </button>

            {/* Back Button */}
            <button
              type="button"
              onClick={onBack}
              className="w-full text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-medium py-2"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Geri Dön
            </button>
          </div>

          {/* Footer Section */}
          <div className="mt-8 text-center space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors">
              © 2025 Satınalma & Talep Yönetim Sistemi. Tüm hakları saklıdır.
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 transition-colors">
              {version}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
